import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Flex, Segmented, Tag, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDateTime } from '../../lib/format';
import { LeadCreateDrawer } from './LeadCreateDrawer';
import { exportLeadsCsv, listLeads, overdueCount, type Lead } from './api';
import { SOURCE_LABEL, STAGE_LABEL, stageTone } from './stage';

const { Text } = Typography;

type View = 'mine' | 'overdue' | 'all';

/**
 * The advisor's day (P1: "my leads, today's follow-ups, one-tap log activity").
 *
 * "My leads" is the default view, not "all leads". An advisor opening this on a
 * phone between customers wants their own list; a manager can switch. Defaulting
 * to everything makes the first screen useless to the person who uses it most.
 */
export function LeadListPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>('mine');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['leads', view, search], (page) =>
    listLeads({
      ...page,
      q: search,
      mine: view === 'mine',
      overdue: view === 'overdue',
    }),
  );

  const { data: overdue } = useQuery({
    queryKey: ['leads', 'overdue-count'],
    queryFn: overdueCount,
  });

  const download = async (): Promise<void> => {
    try {
      const csv = await exportLeadsCsv({ q: search, mine: view === 'mine', overdue: view === 'overdue' });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      // Named after the view, so three exports in a row do not overwrite each other.
      link.download = `leads-${view}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t('sales.createFailed'));
    }
  };

  const emptyFor: Record<View, string> = {
    mine: t('crm.emptyMine'),
    overdue: t('crm.emptyOverdue'),
    all: t('crm.emptyAll'),
  };

  return (
    <>
      <Flex vertical gap={12}>
        <Segmented<View>
          value={view}
          onChange={(value) => {
            setView(value);
          }}
          size="large"
          block
          options={[
            { value: 'mine', label: t('crm.viewMine') },
            {
              value: 'overdue',
              label: (
                <Flex align="center" gap={8} justify="center">
                  {t('crm.viewOverdue')}
                  {(overdue?.count ?? 0) > 0 && <Badge count={overdue?.count} />}
                </Flex>
              ),
            },
            { value: 'all', label: t('crm.viewAll') },
          ]}
        />

        <ListPage<Lead>
          title={t('crm.title')}
          searchPlaceholder={t('crm.search')}
          onSearch={(value) => {
            setSearch(value);
          }}
          loading={isFetching}
          emptyDescription={search.length > 0 ? t('crm.noResults') : emptyFor[view]}
          actions={
            <>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  void download();
                }}
              >
                {t('crm.exportCsv')}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setCreating(true);
                }}
              >
                {t('crm.new')}
              </Button>
            </>
          }
          columns={[
            { title: t('crm.fields.leadNo'), dataIndex: 'leadNo', width: 180 },
            { title: t('fields.name'), dataIndex: 'name' },
            { title: t('fields.phone'), dataIndex: 'phone', width: 150 },
            {
              title: t('crm.fields.stage'),
              dataIndex: 'stage',
              width: 160,
              render: (stage: Lead['stage']) => <Tag color={stageTone(stage)}>{STAGE_LABEL[stage]}</Tag>,
            },
            {
              title: t('crm.fields.source'),
              dataIndex: 'source',
              width: 130,
              render: (source: string) => SOURCE_LABEL[source] ?? source,
            },
            {
              title: t('crm.fields.nextAction'),
              dataIndex: 'nextActionAt',
              width: 190,
              render: (value: string | null, row: Lead) =>
                value === null ? (
                  '—'
                ) : (
                  <Text type={row.isOverdue ? 'danger' : undefined}>{formatDateTime(value)}</Text>
                ),
            },
          ]}
          rows={rows}
          hasMore={hasMore}
          onLoadMore={loadMore}
          rowKey={(row) => row.uid}
          onRowClick={(row) => {
            void navigate(`/leads/${row.uid}`);
          }}
          renderMobileRow={(row) => (
            <Flex vertical gap={4}>
              <Flex justify="space-between" align="center" gap={8}>
                <Text strong>{row.name}</Text>
                <Tag color={stageTone(row.stage)} style={{ marginInlineEnd: 0 }}>
                  {STAGE_LABEL[row.stage]}
                </Tag>
              </Flex>
              <Text type="secondary">{row.phone}</Text>
              {row.nextActionAt !== null && (
                <Text type={row.isOverdue ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                  {row.isOverdue ? t('crm.overdueSince') : t('crm.followUp')}{' '}
                  {formatDateTime(row.nextActionAt)}
                </Text>
              )}
            </Flex>
          )}
        />
      </Flex>

      <LeadCreateDrawer
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(uid, wasExisting) => {
          setCreating(false);
          void queryClient.invalidateQueries({ queryKey: ['leads'] });
          void message.success(wasExisting ? t('crm.joinedExisting') : t('crm.created'));
          void navigate(`/leads/${uid}`);
        }}
      />
    </>
  );
}
